import React, { useContext } from 'react';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import trans from 'translate';
import config from '../../../../../../../../../config/config.json';
import {
  CUSTOM_TOEKN_ADDRESS_FOR_CLOSE_ORDER_KEY,
  manualOrdersMapperSchema,
  manualOrdersTypes,
  typeMapperKeys,
  vpoolTypesMapper,
} from '../../../../../../../../../constants/vpool';
import { usePromise } from '../../../../../../../../../hooks/usePromise/usePromise';
import { assetAmountInWei } from '../../../../../../../../../services/assetsService';
import { getBaseToken } from '../../../../../../../../../services/createHPoolService';
import { getSignatureConfig, updateTradeOrder } from '../../../../../../../../../services/hpoolService';
import { approvedAmountOnOrder, sendManualSwaps } from '../../../../../../../../../services/vpoolService';
import { signTypedDataV4 } from '../../../../../../../../../services/web3Service';
import { combineLoadingMsgs, getTokenSymbol } from '../../../../../../../../../utils/format';
import {
  getValueByMultiProps,
  multiConver,
  objectMapper,
  removeObjKeys,
} from '../../../../../../../../../utils/mapper';
import { createValidation } from '../../../../../../../../../utils/validation';
import { Button, Checkbox, Fields } from '../../../../../../../../Common';
import WalletValidator from '../../../../../../../../hoc/WalletValidator/WalletValidator';
import { OrdersContext } from '../../../../../../../../Modals/EditTokensModal/EditOrderDetails';
import { ManualOrderContext } from '../../../../../../../../Modals/ManualOrderModals/ManualOrderModals';
import { getTokenDecimals } from '../../../../../../../../../services/utilsService';

const fields = [
  {
    name: 'portionId',
    id: 'portionId',
    className: 'w-50 fill-at-mobile',
    label: 'vpool.portion_id',
    tag: 'number',
    placeholder: 0,
  },
  {
    name: 'amountOfTokensToReceive',
    id: 'amountOfTokensToReceive',
    className: 'w-50 fill-at-mobile',
    label: 'vpool.amount_of_tokens_to_return',
    tag: 'number',
    placeholder: 0,
  },
  {
    name: 'tokenAddress',
    id: 'tokenAddress',
    className: 'w-50 fill-at-mobile',
    label: 'vpool.token_address',
    placeholder: 0,
  },
  {
    name: 'tokenToReceiveSymbol',
    id: 'Amount',
    className: 'w-50 fill-at-mobile',
    label: 'vpool.token_to_receive_symbol',
    placeholder: 'BNB',
  },
  // {
  //   name: 'unLockingTime',
  //   id: 'unLockingTime',
  //   className: 'w-50 fill-at-mobile',
  //   label: 'vpool.unlocking_time',
  //   // placeholder: Math.floor(Date.now() / 1000),
  //   renderField: dateTimePicker,
  // },
  {
    name: 'is_last_portion',
    id: 'is_last_portion',
    className: 'w-50 disabled-field d-flex flex-y-end',
    disabled: true,
    renderField: ({ values, setValues, field: { name } }) => (
      <div className="relative">
        <div
          className="d-flex flex-start gap-md mt-3 align-center pointer"
          onClick={() => setValues((_values) => ({ ..._values, [name]: !_values?.[name] }))}
        >
          <Checkbox defaultChecked={values?.[name]} className="green-border" />
          <span className="light-gray-text">{trans('vpool.is_last_portion')}</span>
        </div>
      </div>
    ),
  },
];

const validationSchema = createValidation({
  amountOfTokensToReceive: yup.string().required(' '),
  tokenAddress: yup.string().min(42, 'Invalid Token address').max(42, 'Invalid Token address').required(' '),
});

function ClosePortion({ poolAddress, account }) {
  const { network: chainId, stableCoins } = config;
  const { selectedToken, hpool } = useContext(OrdersContext);

  const { orderInfo, passedValues, onSubmit = () => {} } = useContext(ManualOrderContext);
  const { token_id: passedTokenId, orderId: passedOrderId } = passedValues ?? {};

  const [sign, , signing] = usePromise(signTypedDataV4, { showError: true });
  const [sendOrder, , sendingOrder] = usePromise(sendManualSwaps, { showError: true });
  const [updateOrder, , updatingOrder] = usePromise(updateTradeOrder, { showError: true });
  const [signAmount, , signAmountLoading] = usePromise(approvedAmountOnOrder, {
    showSuccess: trans('vpool.signature_completed'),
  });

  const { id: poolId, assets = [] } = hpool;

  const currentLoading = combineLoadingMsgs([
    { isLoading: signing, loadingText: 'Signing' },
    { isLoading: sendingOrder, loadingText: 'Creating Order' },
    { isLoading: updatingOrder, loadingText: 'Updating Order' },
    { isLoading: signAmountLoading, loadingText: 'Approving' },
  ]);

  const createOrder = (values) => {
    const orderType = manualOrdersTypes.MANUAL_CLOSE_PORTION.toLowerCase();
    const body = {
      is_last_portion: false,
      ...values,
      type: orderType,
      vpoolId: poolId,
      chainId,
    };
    const mappedForRequest = objectMapper(body, manualOrdersMapperSchema);
    return sendOrder(mappedForRequest);
  };

  const retriveStoredCustomToken = () => localStorage.getItem(CUSTOM_TOEKN_ADDRESS_FOR_CLOSE_ORDER_KEY);

  const handleSubmit = async (values) => {
    const { type, primaryType } = getSignatureConfig('ClosePortion', vpoolTypesMapper);
    const tokenDecimals = await getTokenDecimals(values.tokenAddress);
    const approvedAmount = assetAmountInWei(values.amountOfTokensToReceiveNow, tokenDecimals);
    values = multiConver(
      [
        {
          keys: ['unLockingTime', 'portionId', 'amountOfTokensToReceive'], // conver tokenId to number before signing
          convert: (value) => +value,
        },
      ],
      values,
    );

    // Approve the spending of selected tokenAddress
    await signAmount(account, approvedAmount, values.tokenAddress, poolAddress);

    // we just need the tokenAddress for the approve spend, no need for it in signature or request
    delete values.tokenAddress;

    const { trade_order: tradeOrder = orderInfo } = orderInfo ? {} : (await createOrder(values)) || {}; // no need to create the order since order info passed it's just an update process
    const { id: tradeOrderId = passedOrderId, token_id: tokenId = passedTokenId } = tradeOrder;
    const busdDecimals = await getTokenDecimals(config.busd);

    let msg = { tokenId, ...values };

    // Prepare the msg to be valid for signature
    msg = removeObjKeys(msg, typeMapperKeys('ClosePortion'));

    msg = multiConver(
      [
        {
          keys: ['tokenId'], // conver tokenId to number before signing
          convert: (value) => +value,
        },
        {
          keys: ['amountOfTokensToReceive'], // conver amountOfTokensToReceive to wei before signing
          convert: (value) => assetAmountInWei(+value, tokenDecimals),
        },
        {
          keys: ['portionId'], // conver amountOfTokensToReceive to wei before signing
          convert: (value) => assetAmountInWei(+value, busdDecimals),
        },
      ],
      removeObjKeys(msg, typeMapperKeys('ClosePortion')), // => remove unncessary obj key
    );

    const signature = await sign(msg, type, primaryType, true);
    updateOrder(tradeOrderId, { signature, delete: false }).then(onSubmit);
  };

  const { onPrev } = useContext(OrdersContext);

  const handleClick = () => {
    onPrev();
    onSubmit();
  };
  return (
    <Fields fields={fields} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ disabled, errors, defaultWrapperClass, values }) => (
        <div className={defaultWrapperClass}>
          <Button className="button back" onClick={handleClick} text="common.cancel" />
          <WalletValidator>
            <Button
              type="submit"
              className="button green"
              text="common.create"
              loadingText={currentLoading.loadingText}
              loading={currentLoading.isLoading}
              disabled={disabled || currentLoading.isLoading}
              requireWallet
            />
          </WalletValidator>
        </div>
      )}
    </Fields>
  );
}

ClosePortion.propTypes = {
  poolAddress: PropTypes.string.isRequired,
  account: PropTypes.string.isRequired,
};

const mapStateToProps = ({ createHPool, wallet }) => ({
  poolAddress: createHPool.hpool.contract_address,
  account: wallet.account,
});

export default connect(mapStateToProps)(ClosePortion);
